<template>
  <div class="break-glass-page">
    <header class="page-header">
      <h1>Break-Glass</h1>
      <p>Temporarily grant chart access and clinical viewing privileges only.</p>
    </header>

    <div class="toolbar">
      <button class="btn btn-secondary" type="button" @click="loadData" :disabled="loading">
        {{ loading ? "Refreshing..." : "Refresh" }}
      </button>
    </div>

    <div v-if="errorMessage" class="status status--error">{{ errorMessage }}</div>
    <div v-if="successMessage" class="status status--ok">{{ successMessage }}</div>

    <div class="panel">
      <table class="users-table">
        <thead>
          <tr>
            <th>Caregiver</th>
            <th>Username</th>
            <th>Type</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="u in caregiverRows" :key="u.unique_ID">
            <td>{{ displayName(u) }}</td>
            <td>{{ u.username || "-" }}</td>
            <td>{{ u.account_type || "-" }}</td>
            <td>
              <span v-if="u.breakGlassActive" class="tag tag--active">Active</span>
              <span v-else class="tag tag--inactive">Inactive</span>
              <div v-if="u.breakGlassActive && u.incident" class="incident-detail">
                <span>{{ u.incident.organization_wide ? "All charts" : "One chart only" }}</span>
                <span v-if="u.incident.expires_at"> &middot; until {{ formatTime(u.incident.expires_at) }}</span>
                <span v-if="awaitingApproval(u.incident)" class="tag tag--pending">Awaiting second approval</span>
              </div>
            </td>
            <td>
              <button
                v-if="!u.breakGlassActive"
                class="btn btn-danger"
                type="button"
                :disabled="loadingByUser[u.unique_ID]"
                @click="startBreakGlass(u.unique_ID)"
              >
                Activate
              </button>
              <template v-else>
                <button
                  class="btn btn-primary"
                  type="button"
                  :disabled="loadingByUser[u.unique_ID]"
                  @click="endBreakGlass(u.unique_ID)"
                >
                  Revoke
                </button>
                <button
                  v-if="u.incident && awaitingApproval(u.incident)"
                  class="btn btn-secondary"
                  type="button"
                  :disabled="loadingByUser[u.unique_ID]"
                  @click="approveBreakGlass(u)"
                >
                  Approve
                </button>
                <button
                  v-if="u.incident && extendedExpiry(u.incident)"
                  class="btn btn-secondary"
                  type="button"
                  :disabled="loadingByUser[u.unique_ID]"
                  @click="extendBreakGlass(u)"
                >
                  Extend 1 hour
                </button>
              </template>
            </td>
          </tr>
          <tr v-if="!loading && caregiverRows.length === 0">
            <td colspan="5" class="empty">No caregiver users found.</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import { mapGetters } from "vuex";
import { CURR_API } from "@/constants/apis";
import { usePrivilegesStore } from "@/stores/privileges";
import { useSessionSelfStore } from "@/stores/sessionSelf";
import { usePatientContextStore } from "@/stores/patientContext";
import { sessionActorFrom } from "@/utils/sessionIdentity";
import { PRIVILEGES } from "@/constants/privileges";
import { syncPrivilegesAfterSessionChange } from "@/utils/privilegesSessionSync";
import { approveBreakGlass, extendBreakGlass } from "@/api2/breakGlass";

/** The API's ceiling on an incident's life, from its start (BreakGlassIncident::MAX_DURATION_MINUTES). */
const MAX_DURATION_MS = 480 * 60 * 1000;
const EXTENSION_MS = 60 * 60 * 1000;

/** A patient's name goes into the dialog's HTML. */
function escapeHtml(text) {
  return String(text).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

export default {
  name: "BreakGlassPage",
  data() {
    return {
      loading: false,
      users: [],
      statusesByUser: {},
      loadingByUser: {},
      errorMessage: "",
      successMessage: "",
    };
  },
  computed: {
    ...mapGetters("login", {
      allData: "getAllData",
    }),
    accountNumber() {
      return sessionActorFrom(this.allData, useSessionSelfStore().profile).accountnumber;
    },
    actorUniqueId() {
      return sessionActorFrom(this.allData, useSessionSelfStore().profile).unique_ID;
    },
    canAdjustPermissions() {
      return usePrivilegesStore().effectiveGranted(PRIVILEGES.ADJUST_PERMISSIONS);
    },
    openChart() {
      const chart = usePatientContextStore().patient;
      return chart && chart.id ? chart : null;
    },
    caregiverRows() {
      return (this.users || [])
        .filter((u) => String(u.account_type || "").trim().toLowerCase() !== "patient")
        .map((u) => {
          const status = this.statusesByUser[u.unique_ID];
          return {
            ...u,
            breakGlassActive: !!(status && status.is_active),
            incident: status && status.incident ? status.incident : null,
          };
        })
        .sort((a, b) => this.displayName(a).localeCompare(this.displayName(b)));
    },
  },
  methods: {
    normalizePayload(data) {
      if (!data || typeof data !== "object") return {};
      if (Object.prototype.hasOwnProperty.call(data, "status") || Object.prototype.hasOwnProperty.call(data, "data")) {
        return data;
      }
      const keys = Object.keys(data);
      if (keys.length === 1 && data[keys[0]] && typeof data[keys[0]] === "object") {
        return data[keys[0]];
      }
      return data;
    },
    displayName(u) {
      const name = `${u.first_name || ""} ${u.last_name || ""}`.trim();
      return name || u.username || u.unique_ID;
    },
    formatTime(iso) {
      const at = new Date(iso);
      return Number.isNaN(at.getTime()) ? "" : at.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    },
    /** Organization-wide and not yet signed off by a second administrator. */
    awaitingApproval(incident) {
      return Boolean(incident && incident.organization_wide && !incident.approved_by && incident.is_effective);
    },
    /** One more hour, never past the API's ceiling; null when there is no room. */
    extendedExpiry(incident) {
      if (!incident || !incident.is_effective || !incident.started_at || !incident.expires_at) return null;
      const ceiling = new Date(incident.started_at).getTime() + MAX_DURATION_MS;
      const current = new Date(incident.expires_at).getTime();
      const next = Math.min(current + EXTENSION_MS, ceiling);
      return next > current ? new Date(next).toISOString() : null;
    },
    async loadUsers() {
      const body = { Account: { API_Meth: "GetAccountUsers", accountnumber: this.accountNumber } };
      const res = await axios.post(CURR_API + "/tswebhook.php", body, {
        headers: { "Content-Type": "application/json;" },
      });
      const payload = this.normalizePayload(res.data);
      const list = Array.isArray(payload.data) ? payload.data : [];
      this.users = list
        .map((u) => ({
          unique_ID: String(u.subaccountnumber || u.unique_ID || u.subAccountNumber || "").trim(),
          username: String(u.username || "").trim(),
          account_type: String(u.account_type || u.accountType || "").trim(),
          first_name: String(u.firstname || u.Firstname || u.user_Fname || "").trim(),
          last_name: String(u.lastname || u.Lastname || u.user_Lname || "").trim(),
        }))
        .filter((u) => u.unique_ID);
    },
    async loadStatuses() {
      const body = {
        Privileges: {
          API_Meth: "GetBreakGlassStatuses",
          accountnumber: this.accountNumber,
          unique_ID: this.actorUniqueId,
        },
      };
      const res = await axios.post(CURR_API + "/tswebhook.php", body, {
        headers: { "Content-Type": "application/json;" },
      });
      const payload = this.normalizePayload(res.data);
      const map = payload && payload.data && typeof payload.data === "object" ? payload.data : {};
      this.statusesByUser = map;
    },
    async loadData() {
      this.errorMessage = "";
      this.successMessage = "";
      if (!this.canAdjustPermissions) {
        this.errorMessage = "You do not have permission to manage break-glass.";
        return;
      }
      if (!this.accountNumber || !this.actorUniqueId) {
        this.errorMessage = "Unable to resolve account/user identity for this session.";
        return;
      }
      this.loading = true;
      try {
        await this.loadUsers();
        await this.loadStatuses();
      } catch (e) {
        this.errorMessage = "Failed to load break-glass data.";
      } finally {
        this.loading = false;
      }
    },
    /**
     * The justification the API requires, typed by the person activating —
     * never a canned sentence: it is the incident's compliance record (MX-02
     * (5), Trello "prompt to break-glass issuer for text explanation"). With a
     * chart open, the incident can be confined to it (decision D8): a
     * single-chart incident lends its view privileges for that chart only and
     * needs no second approver; an organization-wide one opens at once and
     * waits for another administrator. Resolves `{reason, patientId}`, or null
     * when cancelled.
     */
    async askForActivation() {
      const chart = this.openChart;
      const chartName = chart ? `${chart.first_name || ""} ${chart.last_name || ""}`.trim() || `patient #${chart.id}` : "";
      const result = await Swal.fire({
        icon: "warning",
        title: "Activate break-glass",
        html:
          '<p>Why is emergency access needed? This is recorded and reviewed.</p>'
          + '<textarea id="bg-reason" class="swal2-textarea" minlength="10" maxlength="2000" placeholder="Reason (required)"></textarea>'
          + (chart
            ? `<label class="bg-scope"><input id="bg-chart-only" type="checkbox" checked> Only ${escapeHtml(chartName)}'s chart</label>`
            : "<p>This opens every chart in the organization at once. A second administrator should review and approve it afterwards.</p>"),
        showCancelButton: true,
        confirmButtonText: "Activate",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#dc2626",
        focusConfirm: false,
        preConfirm: () => {
          const reason = String((document.getElementById("bg-reason") || {}).value || "").trim();
          if (reason.length < 10) {
            Swal.showValidationMessage("Type the reason (at least 10 characters).");
            return false;
          }
          const box = document.getElementById("bg-chart-only");
          return { reason, chartOnly: Boolean(box && box.checked) };
        },
      });
      if (!result || !result.isConfirmed || !result.value) return null;
      return {
        reason: result.value.reason,
        patientId: chart && result.value.chartOnly ? chart.id : null,
      };
    },
    async startBreakGlass(targetUniqueId) {
      this.successMessage = "";
      this.errorMessage = "";
      const answer = await this.askForActivation();
      if (!answer) return;
      this.loadingByUser = { ...this.loadingByUser, [targetUniqueId]: true };
      try {
        const body = {
          Privileges: {
            API_Meth: "StartBreakGlass",
            accountnumber: this.accountNumber,
            unique_ID: this.actorUniqueId,
            actor_unique_ID: this.actorUniqueId,
            target_unique_ID: targetUniqueId,
            reason: answer.reason,
            ...(answer.patientId ? { patient_id: answer.patientId } : {}),
          },
        };
        const res = await axios.post(CURR_API + "/tswebhook.php", body, {
          headers: { "Content-Type": "application/json;" },
        });
        const payload = this.normalizePayload(res.data);
        const ok = String(payload.status || "").toLowerCase().includes("success");
        if (!ok) {
          this.errorMessage = payload.message || "Failed to activate break-glass.";
          return;
        }
        this.successMessage = answer.patientId
          ? "Break-glass activated for one chart."
          : "Break-glass activated. A second administrator should approve it.";
        if (String(targetUniqueId) === String(this.actorUniqueId)) {
          syncPrivilegesAfterSessionChange();
        }
        await this.loadStatuses();
      } catch (e) {
        this.errorMessage = "Failed to activate break-glass.";
      } finally {
        this.loadingByUser = { ...this.loadingByUser, [targetUniqueId]: false };
      }
    },
    async endBreakGlass(targetUniqueId) {
      this.successMessage = "";
      this.errorMessage = "";
      const status = this.statusesByUser[targetUniqueId];
      const result = await Swal.fire({
        icon: "question",
        title: "Revoke break-glass",
        input: "text",
        inputPlaceholder: "Reason (optional)",
        showCancelButton: true,
        confirmButtonText: "Revoke",
        cancelButtonText: "Cancel",
      });
      if (!result || !result.isConfirmed) return;
      this.loadingByUser = { ...this.loadingByUser, [targetUniqueId]: true };
      try {
        const body = {
          Privileges: {
            API_Meth: "EndBreakGlass",
            accountnumber: this.accountNumber,
            unique_ID: this.actorUniqueId,
            actor_unique_ID: this.actorUniqueId,
            target_unique_ID: targetUniqueId,
            ...(status && status.incident && status.incident.public_id ? { incident_id: status.incident.public_id } : {}),
            ...(String(result.value || "").trim().length >= 5 ? { reason: String(result.value).trim() } : {}),
          },
        };
        const res = await axios.post(CURR_API + "/tswebhook.php", body, {
          headers: { "Content-Type": "application/json;" },
        });
        const payload = this.normalizePayload(res.data);
        const ok = String(payload.status || "").toLowerCase().includes("success");
        if (!ok) {
          this.errorMessage = payload.message || "Failed to revoke break-glass.";
          return;
        }
        this.successMessage = "Break-glass revoked.";
        if (String(targetUniqueId) === String(this.actorUniqueId)) {
          syncPrivilegesAfterSessionChange();
        }
        await this.loadStatuses();
      } catch (e) {
        this.errorMessage = "Failed to revoke break-glass.";
      } finally {
        this.loadingByUser = { ...this.loadingByUser, [targetUniqueId]: false };
      }
    },
    /**
     * The second administrator's sign-off, in their own request. The API
     * refuses the incident's requester and its subject, and says so.
     */
    async approveBreakGlass(row) {
      await this.incidentAction(row, async () => {
        await approveBreakGlass(row.incident.public_id);
        return "Break-glass approved.";
      }, "Failed to approve break-glass.");
    },
    async extendBreakGlass(row) {
      const expiresAt = this.extendedExpiry(row.incident);
      if (!expiresAt) return;
      await this.incidentAction(row, async () => {
        await extendBreakGlass(row.incident.public_id, { expiresAt });
        return `Break-glass extended until ${this.formatTime(expiresAt)}.`;
      }, "Failed to extend break-glass.");
    },
    async incidentAction(row, run, failure) {
      this.successMessage = "";
      this.errorMessage = "";
      this.loadingByUser = { ...this.loadingByUser, [row.unique_ID]: true };
      try {
        this.successMessage = await run();
        await this.loadStatuses();
      } catch (e) {
        this.errorMessage = (e && e.message) || failure;
      } finally {
        this.loadingByUser = { ...this.loadingByUser, [row.unique_ID]: false };
      }
    },
  },
  /**
   * Warn before break-glass actions that can elevate access to patient data.
   * Proceed loads the page; Go back returns to Administration.
   */
  async mounted() {
    const result = await Swal.fire({
      icon: "warning",
      title: "Warning",
      text: "This page can grant privileges that expose patient data to users.",
      showCancelButton: true,
      confirmButtonText: "Proceed",
      cancelButtonText: "Go back",
      confirmButtonColor: "#4b9ca5",
      cancelButtonColor: "#6c757d",
      allowOutsideClick: false,
      allowEscapeKey: false,
    });
    if (result.isConfirmed) {
      this.loadData();
      return;
    }
    this.$router.push("/administration");
  },
};
</script>

<style scoped lang="scss">
.break-glass-page {
  padding: 1.25rem;
}
.page-header h1 {
  margin: 0;
}
.page-header p {
  margin: 0.35rem 0 1rem;
  color: #5b6472;
}
.toolbar {
  margin-bottom: 0.75rem;
}
.status {
  margin-bottom: 0.65rem;
  padding: 0.55rem 0.7rem;
  border-radius: 8px;
  font-weight: 600;
}
.status--error {
  background: #fee2e2;
  color: #991b1b;
}
.status--ok {
  background: #dcfce7;
  color: #166534;
}
.panel {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  overflow: auto;
}
.users-table {
  width: 100%;
  border-collapse: collapse;
}
.users-table th,
.users-table td {
  padding: 0.7rem;
  border-bottom: 1px solid #f1f5f9;
  text-align: left;
}
.tag {
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 700;
}
.tag--active {
  background: #fef3c7;
  color: #92400e;
}
.tag--inactive {
  background: #e2e8f0;
  color: #334155;
}
.tag--pending {
  margin-left: 0.35rem;
  background: #fee2e2;
  color: #991b1b;
}
.incident-detail {
  margin-top: 0.3rem;
  font-size: 0.82rem;
  color: #5b6472;
}
.users-table td .btn + .btn {
  margin-left: 0.35rem;
}
.btn {
  border: none;
  border-radius: 8px;
  padding: 0.45rem 0.7rem;
  font-weight: 600;
}
.btn-primary {
  background: #0c60ff;
  color: #fff;
}
.btn-danger {
  background: #dc2626;
  color: #fff;
}
.btn-secondary {
  background: #e2e8f0;
  color: #1e293b;
}
.btn:disabled {
  opacity: 0.55;
}
.empty {
  color: #64748b;
  font-style: italic;
}
</style>
